import type {
  ActionComponent,
  ActionComponentStateConfig,
  AnimaticPrimitiveState,
  LabelConfig,
  SummaryConfig,
} from '@repo/sdk/models/components';
import { AnimaticPrimitiveConfigStatus, LabelConfigVariant } from '@repo/sdk/models/components';

import { toLabelFromKind } from '@/helpers/text-inflection-utils';

import {
  DEFAULT_RESET_FAULT_DISABLED_MESSAGE,
  NO_RESET_FAULT_ACTION_MESSAGE,
  RESET_FAULT_STATE_PREFIXES,
} from './faulted-devices.constants';
import type { FaultedDevice } from './faulted-devices.types';

/** True when the animatic's active primitive state is the typed `faulted` status. */
export function isFaultedAnimaticState(state: AnimaticPrimitiveState | undefined): boolean {
  return state?.config.status === AnimaticPrimitiveConfigStatus.Faulted;
}

/** True when the state name belongs to one of the Reset Fault action states. */
export function isResetFaultStateName(stateName: string | undefined): boolean {
  if (!stateName) return false;
  return Object.values(RESET_FAULT_STATE_PREFIXES).some((prefix) => stateName.startsWith(prefix));
}

/** True when the action component carries any Reset Fault state. */
export function hasResetFaultStates(actionComponent: ActionComponent): boolean {
  return (actionComponent.states ?? []).some((state) => isResetFaultStateName(state.stateName));
}

/** Returns the faulted-banner label config of the device's animatic, if it has one. */
export function findFaultedBannerLabel(summary: SummaryConfig): LabelConfig | undefined {
  for (const label of summary.animatic?.labels ?? []) {
    for (const state of label.states) {
      if (state.config?.variant === LabelConfigVariant.FaultedBanner) {
        return state.config;
      }
    }
  }
  return undefined;
}

/**
 * Builds the faulted-device view model for a device whose animatic is faulted.
 *
 * Resettability and writes come from the device's active Reset Fault action state.
 * A device whose active state is `reset-fault-disabled-*` borrows them from the
 * sibling `reset-fault-dropdown-*` state so its current mode can still be shown.
 * A faulted device with no active Reset Fault state is listed but not resettable.
 */
export function buildFaultedDevice(
  summary: SummaryConfig,
  actionComponent?: ActionComponent,
  resetState?: ActionComponentStateConfig
): FaultedDevice {
  if (!actionComponent || !resetState) {
    return {
      disabledMessage: NO_RESET_FAULT_ACTION_MESSAGE,
      faultReasonLabel: findFaultedBannerLabel(summary),
      isResettable: false,
      resetSettingActions: [],
      summary,
    };
  }

  const isResettable = !resetState.stateName.startsWith(RESET_FAULT_STATE_PREFIXES.DISABLED);
  const writesState = isResettable
    ? resetState
    : actionComponent.states.find((state) =>
        state.stateName.startsWith(RESET_FAULT_STATE_PREFIXES.DROPDOWN)
      );
  const writesButtonConfig = writesState?.actionComponentConfig?.buttonComponentActionConfig;
  const resetButtonConfig = resetState.actionComponentConfig?.buttonComponentActionConfig;

  return {
    disabledMessage: isResettable
      ? undefined
      : (resetButtonConfig?.disabledMessage ?? DEFAULT_RESET_FAULT_DISABLED_MESSAGE),
    faultReasonLabel: findFaultedBannerLabel(summary),
    isResettable,
    modeDropdown: writesButtonConfig?.confirmationDialogConfig?.dropdownComponentConfig,
    resetSettingActions: writesButtonConfig?.actions?.settingActions ?? [],
    resetState,
    summary,
  };
}

/** Human-readable equipment type from a device kind (e.g. `screw-compressor` → `Screw Compressor`). */
export function getEquipmentTypeLabel(kind: string | undefined): string {
  return toLabelFromKind(kind ?? '');
}

/** Orders faulted devices by equipment type, then by device title. */
export function sortFaultedDevices(devices: FaultedDevice[]): FaultedDevice[] {
  return [...devices].sort((a, b) => {
    const byType = getEquipmentTypeLabel(a.summary.kind).localeCompare(
      getEquipmentTypeLabel(b.summary.kind)
    );
    if (byType !== 0) return byType;
    return a.summary.title.localeCompare(b.summary.title, undefined, { numeric: true });
  });
}

/**
 * Equality for faulted-device lists. The configs come straight from the view
 * response, so a device is unchanged while its summary and active Reset Fault state
 * are the same objects — live value updates that keep the same state don't count.
 */
export function areFaultedDeviceListsEqual(a: FaultedDevice[], b: FaultedDevice[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((device, index) => isSameFaultedDevice(device, b[index]));
}

/** Equality for one device's faulted entry (`null` when it isn't faulted), as above. */
export function isSameFaultedDevice(a: FaultedDevice | null, b: FaultedDevice | null): boolean {
  if (!a || !b) return a === b;
  return a.summary === b.summary && a.resetState === b.resetState;
}
